import { EventEmitter, Injectable } from '@angular/core';
import { ServiceConfiguration } from './ServiceConfiguration';
import { CashFlowStrategy } from 'projects/shared-components/adjustment-control-panel/cash-flow-strategy';
import { isVoid } from 'projects/shared-components/utils';
import { SolutionPositionDto } from '../model/SolutionPositionDto';
import { CashFlowAdjustment } from '../model/CashFlowAdjustment';
import {MessageBusService} from "../../message-bus.service";
import {UserSettingsService} from "../../user-settings.service";
import {SessionService} from "../../authentication/session-service.service";

/** Published on the message bus after EVERY write to the saved adjustment after-state. */
export const APG_AFTER_STATE_UPDATED_TOPIC = 'Apg.AfterStateUpdated';

export interface ApgAfterStateUpdatedPayload {
   portfolioId: string;
   underlying: string;
   strategy: CashFlowStrategy;
}

const StorageKey = 'apg.positions.after';
const ComparisonStorageKeyLeft = 'cpg.positions.after.left';
const ComparisonStorageKeyRight = 'cpg.positions.after.right';

export interface SavedPositionsAfterState {
   portfolioId: string;
   underlying: string;
   strategy: CashFlowStrategy;
   first?: {
      adjustmentDescriptor: string;
      positions: SolutionPositionDto[];
   };
   second?: {
      adjustmentDescriptor: string;
      positions: SolutionPositionDto[];
   };
}

@Injectable()
export class PositionsAfterStateService {

   constructor(
      private _userSettingsService: UserSettingsService,
      private _messageBus: MessageBusService,
      private _sessionService: SessionService,
   ) {

   }

   private _serviceConfig: ServiceConfiguration;

   afterStateUpdated$ = new EventEmitter();

   //
   configure(cfg: ServiceConfiguration) {
      this._serviceConfig = cfg;
   }

   //
   saveState(
      pricing: CashFlowAdjustment,
      portfolioId: string,
      underlying: string,
      strategy: CashFlowStrategy,
      slot: 'first' | 'second',

      // OPTIONAL. The calling panel's own identity for the item it saved, republished below so that
      // caller can tell which of its items this save belongs to. Not persisted, not read here, and
      // undefined for every caller that does not pass one.
      packageId?: string
   ) {

      const adjustmentId = pricing.logicalId;

      const adjustmentDate = isVoid(pricing.expirationDatePrettyWithDaysToExpiration)
         ? 'N/A'
         : pricing.expirationDatePrettyWithDaysToExpiration.split(' ')[0];

      const positions = pricing.afterState.filter(x => !isVoid(x.ticker));

      const key = this.makeStorageKey(portfolioId, underlying, strategy);
      let state = this._userSettingsService.getValue<SavedPositionsAfterState>(
          key, this._serviceConfig.userId
      );

      if (isVoid(state)) {
         state = {
            portfolioId,
            underlying,
            strategy
         }
      }

      const adjustmentDescriptor = `${adjustmentDate} (${adjustmentId})`;

      if (slot === 'first') {
         state.first = {
            adjustmentDescriptor,
            positions
         };
      } else if (slot === 'second') {
         state.second = {
            adjustmentDescriptor,
            positions
         };
      } else {
         throw new Error('Unknown slot for positions state');
      }

      this._userSettingsService.setValue(key, state, this._serviceConfig?.userId);

      this.afterStateUpdated$.emit();
      this.publishAfterStateUpdated(portfolioId, underlying, strategy);

      this._messageBus.publish({
         topic: 'Pkg-Cmprsn.AfterStateSelected',
         // The payload was empty, so a subscriber could tell that A save happened but never WHOSE.
         // Both existing subscribers take it as a bare signal (`_ =>`, PositionsSectionModel:158 and
         // :162) and re-read, so adding a field cannot reach them.
         //
         // `slot` names the SIDE the save went into ('first' = Calls, 'second' = Puts on a
         // Calls & Puts portfolio) — the hedge pop-up's save already carries it, and Maven's per-side
         // marks read it from both (CR-20260915-003). Not persisted; echoed as received.
         payload: {packageId, slot}
      });
   }

   //
   getState(portfolioId, underlying, strategy): SavedPositionsAfterState {

      const key = this.makeStorageKey(portfolioId, underlying, strategy);

      let index;

      if (this._serviceConfig.userId !== this._sessionService.sessionData.userId) {
         index = 'their-current';
      }

      const state = this._userSettingsService.getValue<SavedPositionsAfterState>(
          key, this._serviceConfig?.userId, index
      );

      return state;
   }

   //
   updateState(state: SavedPositionsAfterState) {
      const key = this.makeStorageKey(
         state.portfolioId,
         state.underlying,
         state.strategy
      );

      this._userSettingsService.setValue(key, state, this._serviceConfig.userId);

      this.afterStateUpdated$.emit();
      this.publishAfterStateUpdated(state.portfolioId, state.underlying, state.strategy);
   }

   //
   removeState(portfolioId: string, underlying: string, strategy: CashFlowStrategy) {
      const key = this.makeStorageKey(
         portfolioId,
         underlying,
         strategy
      );

      this._userSettingsService.deleteValue(key);

      this.afterStateUpdated$.emit();
      this.publishAfterStateUpdated(portfolioId, underlying, strategy);
   }

   // The saved state lives in ONE store (user settings), but `afterStateUpdated$` is an event on
   // THIS INSTANCE — and this service is provided per panel (the Maven panel declares its own so
   // that a super user's reads follow the portfolio owner). So a clear in the APG emitted on the
   // root instance and a panel holding another instance never heard it: its view of the state
   // stayed lit over a slot that was already empty (vrequest 2026-09-13). The message bus is
   // process-wide, which is the property the notification actually needs. Published on every
   // write — save, update, remove — so a subscriber re-reads presence rather than guessing the kind.
   private publishAfterStateUpdated(portfolioId: string, underlying: string, strategy: CashFlowStrategy) {
      this._messageBus.publish<ApgAfterStateUpdatedPayload>({
         topic: APG_AFTER_STATE_UPDATED_TOPIC,
         payload: {portfolioId, underlying, strategy}
      });
   }


   //
   private makeStorageKey(portfolioId: string, underlying: string, strategy: CashFlowStrategy): string {

      console.assert(!isVoid(this._serviceConfig));

      let rootKey = StorageKey;

      if (this._serviceConfig.orientation === 'left') {
         rootKey = ComparisonStorageKeyLeft;
      } else if (this._serviceConfig.orientation === 'right') {
         rootKey = ComparisonStorageKeyRight;
      }

      // CR-20260928-001: every portfolio is real and carries its id in the key. The id-less form
      // (`{root}.{ul}.{strategy}`) was the retired '----' scratchpad's; the backend migration
      // rewrote those rows under the migrated portfolio's id.
      console.assert(!isVoid(portfolioId), 'after-state key needs a portfolio id');

      return `${rootKey}.${portfolioId}.${underlying}.${strategy}`;
   }
}
